import { useEffect, useState } from 'react'
import { Redirect, useParams } from 'react-router'
import {
	BaseWorkflowTaskState,
	WriteWorkflowTaskModel,
} from '../../api/clients/workflowTasks/DTOs'
import WorkflowTaskQueueClient from '../../api/clients/workflowTasks/WorkflowTaskQueueClient'
import WorkflowTasksClient from '../../api/clients/workflowTasks/WorkflowTasksClient'
import { SignalTypeIds } from '../../api/clients/workflows/DTOs'
import WorkflowsClient from '../../api/clients/workflows/WorkflowsClient'
import { FullscreenSpinner } from '../../components/feedback/circular'
import { OcrCorrectionPageWrapper } from '../../modules/Ocr/OcrCorrectionPageWrapper'
import { OcrMode } from '../../modules/Ocr/OcrTypes'
import SessionData from '../../services/session/SessionData'

export type WorkflowTaskResultValues = {
	originalValue: unknown
	newValue: unknown
}

export const WorkflowTaskReviewPage = () => {
	const routeParams = useParams<{
		workflowTaskId: string
	}>()

	const workflowTaskId = parseInt(routeParams.workflowTaskId)
	if (isNaN(workflowTaskId))
		throw new Error(
			`could not parse workflow task id ${routeParams.workflowTaskId}`,
		)

	const [workflowTask, setWorkflowTask] =
		useState<WriteWorkflowTaskModel<BaseWorkflowTaskState>>()
	const [documentReviewed, setDocumentReviewed] = useState<boolean>(false)
	const [nextWorkflowTaskId, setNextWorkflowTaskId] = useState<number>()
	const [queueComplete, setQueueComplete] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)

	const workflowTaskClient = new WorkflowTasksClient()
	const workflowTaskQueueClient = new WorkflowTaskQueueClient()
	const workflowsClient = new WorkflowsClient()

	const getWorkflowTask = async () => {
		const { data } = await workflowTaskClient.getWorkflowTask(workflowTaskId)

		setWorkflowTask(data as WriteWorkflowTaskModel<BaseWorkflowTaskState>)
	}

	const onDocumentReviewed = async (
		outcome: string,
		comparedValues: Record<string, WorkflowTaskResultValues>,
		obj: unknown,
	) => {
		if (workflowTask === undefined) return
		const logMessage = JSON.stringify(comparedValues)

		const userId = SessionData.accessToken.tokenPayload?.sub
		if (userId === undefined)
			throw new Error(
				'invalid state - user id was not present when it should have been in workflow task review page',
			)

		setIsSubmitting(true)

		await workflowsClient
			.createActivitySignal(
				workflowTask.declaredActivityInstanceId,
				SignalTypeIds.OcrDocumentReviewed,
				[
					{
						type: 'WorkflowTask',
						value: {
							id: workflowTask.id,
							lockPassword: workflowTask.lockPassword,
							results: obj,
							userId: userId,
							logMessage: logMessage,
							outcome: outcome,
						},
					},
				],
			)
			.then(async () => {
				await workflowTaskQueueClient
					.getNextInQueue(workflowTask.assignmentTargetId)
					.then((response) => {
						setNextWorkflowTaskId(response.data)
					})
					.catch(() => {
						setQueueComplete(true)
						setNextWorkflowTaskId(undefined)
					})
				setDocumentReviewed(true)
			})
			.finally(() => setIsSubmitting(false))
	}

	useEffect(() => {
		getWorkflowTask()
		setDocumentReviewed(false)
	}, [routeParams.workflowTaskId])

	if (workflowTask === undefined) return <FullscreenSpinner />

	return (
		<>
			{documentReviewed ? (
				queueComplete ? (
					<Redirect to={`/_administration/_tasks`} />
				) : (
					<Redirect to={`/_tasks/${nextWorkflowTaskId}`} />
				)
			) : (
				workflowTask?.workflowTaskState.workflowTaskTypeId ===
					'OcrDocumentReviewWorkflowTask' && (
					<OcrCorrectionPageWrapper
						onDocumentReviewed={onDocumentReviewed}
						isSubmitting={isSubmitting}
						workflowTaskId={workflowTaskId}
						mode={OcrMode.Standard}
					/>
				)
			)}
		</>
	)
}
